import type {
  AiLabelState,
  ArtboardState,
  Box,
  ElementState,
  PageState,
} from 'EditorApp/types'

// Mirrored by `AiLabel` in Ruby (app/models/ai_label.rb), which draws the same
// box into PDF and video exports. Keep the two in step.
const SIZE_FRACTION = 0.05

// The `in` narrows the element union — only images and videos carry
// `aiGenerated`, so the property access does not compile without it. Any
// element type that gains the attribute is picked up here for free.
const isAiGenerated = (element: ElementState) => {
  return 'aiGenerated' in element && !!element.aiGenerated
}

// Whether the page uses any AI generated source asset, and so has to carry the
// label. Derived from the elements rather than stored, so that it follows the
// page as assets are added and removed, and so that it cannot be switched off
// by editing the saved markup.
//
// This is the browser's view of it. Exports re-derive the same answer from the
// database, which is the authoritative one.
export const pageHasAiGeneratedContent = (page: PageState) => {
  return Object.values(page.elements).some(isAiGenerated)
}

export const hasAiGeneratedContent = (markup: ArtboardState) => {
  return Object.values(markup.pages).some(pageHasAiGeneratedContent)
}

// The label to draw on the page, or null when the page does not need one. Each
// page is labelled for its own assets alone, whatever the rest of the artboard
// uses.
export const aiLabelForPage = (
  artboard: ArtboardState,
  page: PageState
): AiLabelState | null => {
  return pageHasAiGeneratedContent(page) ? artboard.aiLabel : null
}

// A square label sized against the shortest side of the page, inset from the
// edge by half its own size. Page space, top-left origin — which is markup px,
// PDF points and video pixels alike, since PageDimensions::PX_PER_INCH is 72.
export const aiLabelBox = (
  aiLabel: AiLabelState,
  pageWidth: number,
  pageHeight: number
): Box => {
  const size = SIZE_FRACTION * Math.min(pageWidth, pageHeight)
  const margin = size / 2

  return {
    left: pageWidth - margin - size,
    top: aiLabel.position === 'top-right' ? margin : pageHeight - margin - size,
    width: size,
    height: size,
  }
}
